import React, { useCallback, useEffect, useState } from 'react';
import { Input, message, AutoComplete, Layout } from 'antd';
import { useNavigate } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client'; // Apollo hooks for GraphQL query

const { Header } = Layout;

// ... (GraphQL queries remain the same)
const SEARCH_ARTICLES_QUERY = gql`
  query listApprovedArticles($query: String!) {
    listApprovedArticles(
      where: {
        OR: [
          { title: { contains: $query, mode: insensitive } }
          { tags: { contains: $query, mode: insensitive } }
        ]
      }
    ) {
      id
      title
      tags
      createdAt
    }
  }
`;

const SEARCH_PUBLIC_ARTICLES_QUERY = gql`
  query listPublicArticles($query: String!) {
    listPublicArticles(
      where: {
        OR: [
          { title: { contains: $query, mode: insensitive } }
          { tags: { contains: $query, mode: insensitive } }
        ]
      }
    ) {
      id
      title
      tags
      createdAt
    }
  }
`;


const HeroSection = () => {
    const [options, setOptions] = useState([]); // For search suggestions
    const [searchQuery, setSearchQuery] = useState(''); // For storing the search query
    const navigate = useNavigate(); // To navigate to article details
    const [isAuth, setIsAuth] = useState(false); // Track authentication status
    

 // Check if user is authenticated on component mount
 useEffect(() => {
  setIsAuth(Boolean(localStorage.getItem('authToken')));
}, []);


const processSearchResults = useCallback((data) => {
  const articles = isAuth 
    ? data?.listApprovedArticles || [] 
    : data?.listPublicArticles || [];
  
  const searchResults = articles.map((article) => ({
    value: article.id,
    title: article.title,   
    label: (
      <div>
        <strong>{article.title}</strong> <br />
        <small>{new Date(article.createdAt).toLocaleDateString()}</small>
      </div>
    ),
  }));
  
  setOptions(searchResults);
}, [isAuth]);

    const queryToUse = isAuth ? SEARCH_ARTICLES_QUERY : SEARCH_PUBLIC_ARTICLES_QUERY;

    const [searchArticles, { loading }] = useLazyQuery(queryToUse, {
      onCompleted: processSearchResults,
      onError: (error) => {
        console.error('Error fetching search results:', error);
        message.error('Failed to fetch articles');
      },
    });

    
    const onSearch = (value) => {
      setSearchQuery(value);
      if (value) {
        searchArticles({ variables: { query: value } });
      } else {
        setOptions([]);
      }
    };

    const onSelect = (articleId) => {
      setOptions([]);
      console.log('Selected article ID:', articleId);
      navigate(`/articles/${articleId}`);
    };

    const onSubmit = () => {
      if (searchQuery.trim() !== '') {
        console.log('Submitting search:', searchQuery);
        navigate(`/search/${searchQuery}`);
        setOptions([]);
      }
    };

    return (
      <div style={styles.heroContainer}>
        <div style={styles.contentBox}>
          <h1 style={styles.heading}>GBN Open Knowledge Base</h1>
          <p style={styles.subHeading}>
            Discover, collaborate, and contribute to an open knowledge base for the community.
          </p>
          
          {/* 1. We make the container a flexbox to align items horizontally */}
          <div 
            className="fsearch-bar" 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px', 
              justifyContent: 'center' 
            }}
          >
            <AutoComplete
              options={options}
              onSearch={onSearch}
              onSelect={onSelect}
              value={searchQuery}
              style={styles.searchInput}
            >
              <Input.Search
                placeholder="Search articles..."
                loading={loading}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onSearch={onSubmit} // This is the key function
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    onSubmit();
                  }
                }}
              />
            </AutoComplete>
            
            {/* 2. Add a clickable "Filters" span */}
            <span 
              onClick={onSubmit} // We call the *exact same* onSubmit function
              style={{ 
                color: '#FFFFFF', 
                cursor: 'pointer',
                fontWeight: 'bold',
                textDecoration: 'underline',
                whiteSpace: 'nowrap' // Prevents text from wrapping
              }}
            >
              Filters
            </span>
          </div>
        </div>
      </div>
    );
};

const styles = {
    heroContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '40vh',
        backgroundColor: '#61B15A',
    },
    contentBox: {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        padding: '40px',
        borderRadius: '12px',
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
    },
    heading: {
        color: '#FFFFFF',
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '10px',
    },
    subHeading: {
        color: '#FFFFFF',
        fontSize: '1rem',
        marginBottom: '20px',
    },
    searchInput: {
        width: '100%',
        maxWidth: '400px',
        borderRadius: '5px',
    },
};

export default HeroSection;